import { useRetainedRealtimeState } from '../../services/firebase/use-retained-realtime-state'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import type { AppointmentView } from './appointments-model'
import {
  mergeAppointmentPages,
  parseAppointmentPage,
  parseReviewPage,
  type AppointmentPage,
} from './appointments-pagination'
import {
  appointmentReadError,
  type AppointmentsRepository,
} from './appointments-repository'

type AppointmentsState = {
  owner: string | null
  view: AppointmentView
  status: 'loading' | 'ready' | 'forbidden' | 'error' | 'invalid'
  newest: AppointmentPage | null
  historical: AppointmentPage[]
  review: AppointmentPage | null
  primaryLoaded: boolean
  reviewLoaded: boolean
  loadingMore: boolean
}

function emptyState(
  owner: string | null,
  view: AppointmentView
): AppointmentsState {
  return {
    owner,
    view,
    status: 'loading',
    newest: null,
    historical: [],
    review: null,
    primaryLoaded: false,
    reviewLoaded: false,
    loadingMore: false,
  }
}

export function useAppointments(
  repository: AppointmentsRepository,
  tenantId: string | null,
  view: AppointmentView,
  nowMs: number,
  includeReview = true
) {
  const generation = useRef(0)
  const [state, setState, resume] = useRetainedRealtimeState<AppointmentsState>(
    ['appointments', tenantId, view, includeReview],
    { ...emptyState(tenantId, view), reviewLoaded: !includeReview },
    (value) => value.status === 'ready'
  )

  useEffect(() => {
    const currentGeneration = ++generation.current
    resume()
    setState((current) => ({ ...current, loadingMore: false }))
    if (!tenantId) return

    const handleError = (error: unknown) => {
      if (generation.current !== currentGeneration) return
      setState({
        owner: tenantId,
        view,
        status:
          appointmentReadError(error) === 'forbidden' ? 'forbidden' : 'error',
        newest: null,
        historical: [],
        review: null,
        primaryLoaded: false,
        reviewLoaded: !includeReview,
        loadingMore: false,
      })
    }
    // An availability error clears when the reads deliver again; a refusal of
    // access stays until the list is opened again.
    const unsubscribePrimary = repository.subscribeAppointments(
      tenantId,
      view,
      nowMs,
      (pageValue) => {
        if (generation.current !== currentGeneration) return
        const page = parseAppointmentPage(pageValue, view)
        setState((current) => {
          if (current.status === 'forbidden') return current
          return {
            ...current,
            status: !page.validCollection
              ? 'invalid'
              : current.reviewLoaded
                ? !includeReview || current.review?.validCollection
                  ? 'ready'
                  : 'invalid'
                : 'loading',
            newest: page.validCollection ? page : null,
            historical: page.validCollection ? current.historical : [],
            primaryLoaded: true,
          }
        })
      },
      handleError
    )
    const unsubscribeReview = includeReview
      ? repository.subscribeAppointmentsNeedingReview(
          tenantId,
          (reviewValue) => {
            if (generation.current !== currentGeneration) return
            const review = parseReviewPage(reviewValue)
            setState((current) => {
              if (current.status === 'forbidden') return current
              return {
                ...current,
                status: !review.validCollection
                  ? 'invalid'
                  : current.primaryLoaded
                    ? current.newest?.validCollection
                      ? 'ready'
                      : 'invalid'
                    : 'loading',
                review: review.validCollection ? review : null,
                reviewLoaded: true,
              }
            })
          },
          handleError
        )
      : () => undefined

    return () => {
      generation.current += 1
      unsubscribePrimary()
      unsubscribeReview()
    }
  }, [includeReview, nowMs, repository, tenantId, view, resume, setState])

  const effectiveState =
    state.owner === tenantId && state.view === view
      ? state
      : emptyState(tenantId, view)
  const appointments = useMemo(
    () =>
      mergeAppointmentPages(
        [
          ...effectiveState.historical,
          ...(effectiveState.newest ? [effectiveState.newest] : []),
        ],
        view
      ),
    [effectiveState.historical, effectiveState.newest, view]
  )
  const lastPage =
    effectiveState.historical.at(-1) ?? effectiveState.newest ?? null

  const loadMore = useCallback(async () => {
    if (
      !tenantId ||
      !lastPage?.hasMore ||
      !lastPage.cursor ||
      effectiveState.loadingMore
    ) {
      return
    }
    const requestGeneration = generation.current
    setState((current) => ({ ...current, loadingMore: true }))
    try {
      const value = await repository.getMoreAppointments(
        tenantId,
        view,
        lastPage.cursor
      )
      if (generation.current !== requestGeneration) return
      const page = parseAppointmentPage(value, view)
      setState((current) => ({
        ...current,
        status: page.validCollection ? 'ready' : 'invalid',
        newest: page.validCollection ? current.newest : null,
        historical: page.validCollection ? [...current.historical, page] : [],
        loadingMore: false,
      }))
    } catch (error) {
      if (generation.current !== requestGeneration) return
      setState((current) => ({
        ...current,
        status:
          appointmentReadError(error) === 'forbidden' ? 'forbidden' : 'error',
        loadingMore: false,
      }))
    }
  }, [
    effectiveState.loadingMore,
    lastPage,
    repository,
    tenantId,
    view,
    setState,
  ])

  return {
    status: effectiveState.status,
    appointments,
    reviewAppointments: effectiveState.review?.items ?? [],
    reviewHasMore: effectiveState.review?.hasMore ?? false,
    invalidCount:
      (effectiveState.newest?.invalidCount ?? 0) +
      effectiveState.historical.reduce(
        (total, page) => total + page.invalidCount,
        0
      ) +
      (effectiveState.review?.invalidCount ?? 0),
    hasMore: lastPage?.hasMore ?? false,
    loadingMore: effectiveState.loadingMore,
    loadMore,
  }
}
